// V1.41 · Guia visual previa a la captura de documentos.
//
// Objetivo: reducir fotos mal encuadradas SIN cambiar el flujo (foto → adjuntar
// → enviar) ni tocar el motor de captura/OCR. Se muestra una unica pantalla
// modal ANTES de abrir la cámara nativa (`<input capture>`), con un marco
// de referencia del documento a capturar y consejos estáticos.
//
// Uso:
//   const [guide, setGuide] = useState(false);
//   ...
//   <button onClick={() => setGuide(true)}>Hacer foto</button>
//   <CaptureGuideDialog
//     open={guide}
//     docType="dni-front"
//     onCancel={() => setGuide(false)}
//     onContinue={() => { setGuide(false); nativeInputRef.current?.click(); }}
//   />
//
// El componente NO abre la camara. Solo enseña la guia y devuelve el control
// al padre en `onContinue`. La camara nativa la sigue disparando el padre
// con `<input type="file" capture>` como hasta ahora.
import React from "react";
import { Camera, X, Info } from "lucide-react";

// Presets: proporción del marco (aspect = width/height) + textos.
// - DNI en horizontal → 85.6/54 ≈ 1.585
// - A4 en vertical (factura, mod12) → 210/297 ≈ 0.707
// - A4 en apaisado (contrato, psicotécnico) → 297/210 ≈ 1.414
// - Cuadrado (ubicación / fachada) → 1.0
const PRESETS = {
  "dni-front": {
    title: "DNI · Anverso",
    aspect: 1.585,
    hint: "Coloque el DNI dentro del marco, con la cara y los datos hacia la cámara.",
    tips: [
      "Iluminación uniforme, sin sombras propias.",
      "Que el DNI ocupe casi todo el marco, centrado.",
      "Evita reflejos sobre el plástico.",
    ],
  },
  "dni-back": {
    title: "DNI · Reverso",
    aspect: 1.585,
    hint: "Coloque el reverso del DNI dentro del marco, con la banda MRZ legible.",
    tips: [
      "Que se lea claramente el bloque de números de abajo.",
      "Documento centrado y a la misma altura de la cámara.",
      "Sin reflejos ni recortes en los bordes.",
    ],
  },
  "psicotecnico": {
    title: "Certificado psicotécnico",
    aspect: 0.707, // A4 vertical
    hint: "Coloque el certificado completo dentro del marco.",
    tips: [
      "Fondo liso, mesa o carpeta neutra.",
      "Que se vea todo el papel, incluidos los sellos.",
      "Sin dedos ni sombras sobre el texto.",
    ],
  },
  "factura": {
    title: "Factura / Ticket",
    aspect: 0.707,
    hint: "Coloque la factura completa dentro del marco.",
    tips: [
      "Papel plano, sin arrugas ni pliegues.",
      "Que se lea el total y el CIF/NIF del emisor.",
      "Evita luces reflejadas sobre el papel.",
    ],
  },
  "mod12": {
    title: "Modelo 12 (DGT)",
    aspect: 0.707,
    hint: "Coloque el impreso completo dentro del marco.",
    tips: [
      "Documento sobre una superficie plana.",
      "Que se lean nombre, apellidos y firma.",
      "Sin sombras ni dedos en el borde.",
    ],
  },
  "contrato": {
    title: "Contrato / Documento",
    aspect: 0.707,
    hint: "Coloque la hoja completa dentro del marco.",
    tips: [
      "Fondo liso y contrastado con el papel.",
      "Toda la página visible, sin recortes.",
      "Nítido y sin movimiento.",
    ],
  },
  "ubicacion": {
    title: "Fachada / Ubicación",
    aspect: 1.0,
    hint: "Coloque el punto de referencia dentro del marco.",
    tips: [
      "Se debe reconocer la fachada o entrada.",
      "Encuadre estable, sin inclinar el móvil.",
    ],
  },
};

export default function CaptureGuideDialog({
  open,
  docType = "factura",
  onCancel,
  onContinue,
}) {
  if (!open) return null;
  const preset = PRESETS[docType] || PRESETS.factura;
  // El marco lo dibujamos con un padding-bottom en % para respetar el aspect
  // en cualquier tamaño de pantalla — 100/aspect es la altura relativa.
  const framePb = (100 / preset.aspect).toFixed(2);
  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="capture-guide-dialog"
      className="fixed inset-0 z-[1300] bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6"
    >
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[95vh]">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-neutral-200">
          <Camera className="h-4 w-4 text-orange-600" />
          <div className="min-w-0">
            <div className="text-sm font-bold">{preset.title}</div>
            <div className="text-[11px] text-neutral-500">Guía de encuadre</div>
          </div>
          <button
            type="button"
            onClick={() => onCancel?.()}
            data-testid="capture-guide-close"
            className="ml-auto rounded-full p-1.5 hover:bg-neutral-100 text-neutral-500"
            aria-label="Cerrar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-4 flex-1 overflow-y-auto space-y-3">
          {/* MARCO GUÍA — dashed border con aspecto real del documento. */}
          <div className="w-full rounded-2xl bg-neutral-100 overflow-hidden" data-testid="capture-guide-frame-wrap">
            <div
              className="relative w-full"
              style={{ paddingBottom: `${framePb}%` }}
            >
              <div className="absolute inset-3 sm:inset-4 rounded-xl border-2 border-dashed border-orange-400 bg-white/60 flex items-center justify-center text-center">
                <div className="text-neutral-500 text-xs sm:text-sm font-semibold px-3">
                  Coloque aquí
                  <br />
                  el documento
                </div>
              </div>
              {/* Esquinas resaltadas para reforzar la referencia visual */}
              <span className="absolute top-3 left-3 sm:top-4 sm:left-4 h-4 w-4 border-t-2 border-l-2 border-orange-600 rounded-tl-sm" />
              <span className="absolute top-3 right-3 sm:top-4 sm:right-4 h-4 w-4 border-t-2 border-r-2 border-orange-600 rounded-tr-sm" />
              <span className="absolute bottom-3 left-3 sm:bottom-4 sm:left-4 h-4 w-4 border-b-2 border-l-2 border-orange-600 rounded-bl-sm" />
              <span className="absolute bottom-3 right-3 sm:bottom-4 sm:right-4 h-4 w-4 border-b-2 border-r-2 border-orange-600 rounded-br-sm" />
            </div>
          </div>

          <div className="rounded-xl bg-orange-50 border border-orange-200 px-3 py-2 flex items-start gap-2 text-[11px] text-orange-900">
            <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <div>{preset.hint}</div>
          </div>

          <ul className="space-y-1 text-[11px] text-neutral-600 pl-1">
            {preset.tips.map((t, i) => (
              <li key={i} className="flex items-start gap-1.5">
                <span className="mt-1 h-1 w-1 rounded-full bg-neutral-400 shrink-0" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="border-t border-neutral-200 px-3 py-3 flex items-center gap-2">
          <button
            type="button"
            onClick={() => onCancel?.()}
            data-testid="capture-guide-cancel"
            className="btn-secondary !h-10 !px-4 !text-xs"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => onContinue?.()}
            data-testid="capture-guide-continue"
            className="btn-primary !h-10 !px-4 !text-xs ml-auto"
          >
            <Camera className="h-3.5 w-3.5" /> Abrir cámara
          </button>
        </div>
      </div>
    </div>
  );
}
